// ./src/contexts/TireSetsContext.tsx
//
// Context wrapper around the useTireSets hook.
//
// Purpose:
// - Allows sibling components (list, edit drawer, swap drawer, toolbar)
//   to share the same tire sets state without prop-drilling.
// - Each page creates its own <TireSetsProvider> with its own config,
//   so TireSetPage filters don't affect DashboardPage, etc.
// - The underlying useTireSets hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <TireSetsProvider autoLoad={false}>
//     <TireSetPageContent />
//     <TireSetEditDrawer />
//   </TireSetsProvider>
//
//   // In any child component:
//   const { tireSets, isLoading, create, ... } = useTireSetsContext();
//
//   // In a component that may or may not be inside a provider (e.g. drawer):
//   const context = useOptionalTireSetsContext();
//   const createFn = context?.create ?? props.onCreate;

import React, { createContext, useContext } from "react";

import { useTireSets, UseTireSetsOptions, UseTireSetsReturn } from "@/hooks/useTireSets";

// ============================================
// Context
// ============================================

const TireSetsContext = createContext<UseTireSetsReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface TireSetsProviderProps extends UseTireSetsOptions {
  children: React.ReactNode;
}

/**
 * Wraps useTireSets into a context provider.
 *
 * Each instance creates its own independent useTireSets store
 * with its own filters, pagination, loading state, and error state.
 *
 * @example
 * <TireSetsProvider autoLoad={false} carId={selectedCarId}>
 *   <MyPageContent />
 * </TireSetsProvider>
 */
export const TireSetsProvider: React.FC<TireSetsProviderProps> = ({
  children,
  ...options
}) => {
  const tireSets = useTireSets(options);

  return (
    <TireSetsContext.Provider value={tireSets}>
      {children}
    </TireSetsContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the TireSetsContext. Throws if used outside a TireSetsProvider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. TireSetList, TireSetPage content).
 */
export const useTireSetsContext = (): UseTireSetsReturn => {
  const context = useContext(TireSetsContext);

  if (!context) {
    throw new Error(
      "useTireSetsContext must be used within a <TireSetsProvider>. " +
      "Wrap your component tree with <TireSetsProvider> or use useOptionalTireSetsContext() instead."
    );
  }

  return context;
};

/**
 * Access the TireSetsContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. TireSetEditDrawer which works both inside TireSetPage's provider
 * and standalone on DashboardPage).
 *
 * @example
 * const context = useOptionalTireSetsContext();
 * const createFn = context?.create ?? props.onCreate;
 * const loadFn = context?.get ?? props.onLoad;
 */
export const useOptionalTireSetsContext = (): UseTireSetsReturn | null => {
  return useContext(TireSetsContext);
};